import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSlideContent, readSlideContentFromLocalStorage } from '../utils/googleDrive';
import { mergeAuthFromChildWindowUrlParam } from '../utils/googleAuth';
import { useFitTextToBox } from '../hooks/useFitTextToBox';
import { useRemoteJoinAlerts } from '../hooks/useRemoteJoinAlerts';
import DualGoogleSlidesEmbed from './DualGoogleSlidesEmbed';
import { advancePlaylistToNextDeckIfAtLastSlide } from '../utils/playlistAdvanceFromLocalStorage';
import { broadcastCurrentSlideState, DECKPACK_CURRENT_SLIDE_EVENT } from '../utils/currentSlideBroadcast';
import '../styles/PresenterView.css';

function PresenterView() {
  const [searchParams] = useSearchParams();
  const initialPresentationId = searchParams.get('presentationId');
  const authParam = searchParams.get('auth');
  const slideIndexParam = searchParams.get('slideIndex');
  const [currentSlideIndex, setCurrentSlideIndex] = useState(
    slideIndexParam ? parseInt(slideIndexParam) : 0
  );
  /** Notes + prev/next thumbnails track the visible iframe after load. */
  const [chromeSlideIndex, setChromeSlideIndex] = useState(
    slideIndexParam ? parseInt(slideIndexParam) : 0
  );
  const currentSlideIndexRef = useRef(currentSlideIndex);
  currentSlideIndexRef.current = currentSlideIndex;
  const [activePresentationId, setActivePresentationId] = useState(initialPresentationId);
  const [slideContent, setSlideContent] = useState(null);
  const slideContentRef = useRef(slideContent);
  slideContentRef.current = slideContent;
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(true); // Auto-start
  const [systemTime, setSystemTime] = useState(new Date());
  const [autoAdvance, setAutoAdvance] = useState(false);
  const autoAdvanceRef = useRef(autoAdvance);
  autoAdvanceRef.current = autoAdvance;
  // Add notification state
  const [notification, setNotification] = useState({
    message: '',
    type: '',
    visible: false
  });
  const overlayToastHideTimeoutRef = useRef(null);
  const presenterRemoteModalClosedRef = useRef(false);
  const openRemoteFromPresenter = useCallback(() => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: 'deckpack-open-remote-modal' }, window.location.origin);
        window.opener.focus();
      }
    } catch {
      /* ignore */
    }
  }, []);

  useRemoteJoinAlerts({
    session: null,
    remoteModalOpenRef: presenterRemoteModalClosedRef,
    onOpenRemoteModal: openRemoteFromPresenter,
    setPendingCount: undefined,
    joinAlertPrefEpoch: 0,
  });

  const presenterSlidesSrc = useMemo(() => {
    if (!activePresentationId) return '';
    return `https://docs.google.com/presentation/d/${activePresentationId}/preview?rm=minimal&delayms=0&loop=false&speed=1&slide=${currentSlideIndex + 1}&nav=0&start=false`;
  }, [activePresentationId, currentSlideIndex]);

  const presenterNotesRef = useRef(null);
  const notesContentRef = useRef(null);
  const presenterSlidesEmbedRef = useRef(null);
  const onPresenterEmbedVisibleReady = () => {
    setChromeSlideIndex(currentSlideIndexRef.current);
  };

  // Blur Slides embeds when clicking outside them so keyboard shortcuts reach the presenter window.
  useEffect(() => {
    const onPointerDownCapture = (e) => {
      const t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('iframe.slides-embed-pane')) return;
      if (t.tagName === 'IFRAME' && t.classList?.contains('slides-embed-pane')) return;
      try {
        presenterSlidesEmbedRef.current?.blurEmbeds?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => window.removeEventListener('pointerdown', onPointerDownCapture, true);
  }, []);

  // Console / remote “Reveal” bumps `deckpack-embed-reveal` so this window mirrors in-slide builds.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'deckpack-embed-reveal' || !e.newValue) return;
      try {
        presenterSlidesEmbedRef.current?.nudgeInSlideAdvance?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useFitTextToBox(
    presenterNotesRef,
    notesContentRef,
    `${activePresentationId ?? ''}-${chromeSlideIndex}-${slideContent?.slides ? 'ready' : 'pending'}`,
    { minFontSize: 4, maxFontSize: 32 }
  );

  // Tell control panel that presenter view is open (so clock button stays enabled after control-panel refresh)
  useEffect(() => {
    const key = 'presenter-view-open';
    localStorage.setItem(key, '1');
    const clear = () => localStorage.removeItem(key);
    window.addEventListener('beforeunload', clear);
    window.addEventListener('pagehide', clear);
    return () => {
      clear();
      window.removeEventListener('beforeunload', clear);
      window.removeEventListener('pagehide', clear);
    };
  }, []);

  // Reset timer when presentation changes
  useEffect(() => {
    setTime(0);
    setIsRunning(true);
  }, [activePresentationId]);

  // Update system time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setSystemTime(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Fix the timer increment logic to avoid double counting
  useEffect(() => {
    let timerInterval;
    
    if (isRunning) {
      timerInterval = setInterval(() => {
        setTime(prevTime => prevTime + 1);
      }, 1000);
    }
    
    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [isRunning]);

  // Sync stopwatch to localStorage so the control panel / remote relay can mirror it (same-tab reads).
  useEffect(() => {
    try {
      localStorage.setItem(
        'presenter-timer-sync',
        JSON.stringify({ elapsedSec: time, running: isRunning, ts: Date.now() })
      );
    } catch {
      /* ignore */
    }
  }, [time, isRunning]);

  useEffect(() => {
    const loadSlideContent = () => {
      if (authParam) mergeAuthFromChildWindowUrlParam(authParam);
      const savedAuth = localStorage.getItem('user_auth');
      const auth = savedAuth ? JSON.parse(savedAuth) : null;

      if (auth?.access_token && activePresentationId) {
        const cached = readSlideContentFromLocalStorage(activePresentationId);
        if (cached?.slides?.length) {
          setSlideContent(cached);
        }
        getSlideContent(auth.access_token, activePresentationId).then(setSlideContent);
      } else {
        console.error('Missing auth or presentationId', { auth: !!auth, presentationId: activePresentationId });
      }
    };

    loadSlideContent();

    const onStorage = (e) => {
      if (e.key === 'user_auth' && e.newValue) {
        loadSlideContent();
        return;
      }
      if (!activePresentationId || !e.key || !e.newValue) return;
      const legacyKey = `slide-content-${activePresentationId}`;
      const sizedPrefix = `slide-content-${activePresentationId}-`;
      if (e.key === legacyKey || e.key.startsWith(sizedPrefix)) {
        const fresh = readSlideContentFromLocalStorage(activePresentationId);
        if (fresh?.slides?.length) {
          setSlideContent(fresh);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [activePresentationId, authParam]);

  // Watch for slide changes from localStorage - control panel drives navigation
  useEffect(() => {
    document.title = "Deck Pack - Speaker Notes";
    let lastObservedIndex = -1;
    let lastObservedPresentationId = null;

    const applyState = (state) => {
      if (!state || !state.presentationId) return;
      const index = typeof state.index === 'number' ? state.index : 0;
      const presentationChanged = state.presentationId !== lastObservedPresentationId;
      const indexChanged = index !== lastObservedIndex;
      if (!presentationChanged && !indexChanged) return;

      lastObservedIndex = index;
      lastObservedPresentationId = state.presentationId;
      setCurrentSlideIndex(index);
      setActivePresentationId(state.presentationId);
      // Notes + sidebar thumbnails follow `chromeSlideIndex`; keep in sync on every index change.
      setChromeSlideIndex(index);
    };

    const checkForUpdates = () => {
      const stateStr = localStorage.getItem('current-slide-state');
      if (!stateStr) return;
      try {
        const state = JSON.parse(stateStr);
        applyState(state);
      } catch (error) {
        console.error("Error parsing state:", error);
      }
    };

    // Initial sync
    checkForUpdates();

    // React to cross-window storage events
    const handleStorageChange = (e) => {
      if (e.key === 'current-slide-state' && e.newValue) {
        try {
          const state = JSON.parse(e.newValue);
          applyState(state);
        } catch (error) {
          console.error("Error parsing storage event state:", error);
        }
      }
    };

    const onSameTabBroadcast = (ev) => {
      if (ev?.detail) applyState(ev.detail);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(DECKPACK_CURRENT_SLIDE_EVENT, onSameTabBroadcast);

    // Poll as a fallback (same-tab updates do not fire `storage`)
    const interval = setInterval(checkForUpdates, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(DECKPACK_CURRENT_SLIDE_EVENT, onSameTabBroadcast);
      clearInterval(interval);
    };
  }, []);

  // Add listener for timer reset and overlays via localStorage
  useEffect(() => {
    const clearOverlayToastHide = () => {
      if (overlayToastHideTimeoutRef.current != null) {
        clearTimeout(overlayToastHideTimeoutRef.current);
        overlayToastHideTimeoutRef.current = null;
      }
    };

    const handleTimerReset = () => {
      const timerStateStr = localStorage.getItem('presenter-timer-state');
      if (timerStateStr) {
        try {
          const timerState = JSON.parse(timerStateStr);
          // Check if this is a reset message
          if (timerState.reset) {
            // Reset the timer immediately
            setTime(0);
            setIsRunning(true);
            
            // Show notification
            setNotification({
              message: 'Timer Reset',
              type: 'info',
              visible: true
            });
            
            // Hide after 2 seconds
            setTimeout(() => {
              setNotification(prev => ({...prev, visible: false}));
            }, 2000);
          }
        } catch (error) {
          console.error("Error parsing timer state:", error);
        }
      }
    };
    
    // Add storage event listener for changes from other windows
    const handleStorageChange = (e) => {
      if (e.key === 'presenter-timer-state') {
        handleTimerReset();
      } else if (e.key === 'presentation-overlay') {
        try {
          const overlayData = JSON.parse(e.newValue);
          clearOverlayToastHide();
          if (overlayData.type === 'black') {
            setNotification({
              message: 'Blackout Active',
              type: 'active',
              visible: true
            });
          } else if (overlayData.type === 'white') {
            setNotification({
              message: 'Whiteout Active',
              type: 'active',
              visible: true
            });
          } else if (overlayData.type === 'none') {
            setNotification({
              message: 'Screen Normal',
              type: 'info',
              visible: true
            });
            
            overlayToastHideTimeoutRef.current = window.setTimeout(() => {
              overlayToastHideTimeoutRef.current = null;
              setNotification(prev => ({...prev, visible: false}));
            }, 2000);
          }
        } catch (error) {
          console.error("Error parsing overlay data:", error);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Check immediately on mount for any existing states
    handleTimerReset();
    
    // Also check for any active overlay
    try {
      const overlayStr = localStorage.getItem('presentation-overlay');
      if (overlayStr) {
        const overlayData = JSON.parse(overlayStr);
        if (overlayData.type === 'black') {
          setNotification({
            message: 'Blackout Active',
            type: 'active',
            visible: true
          });
        } else if (overlayData.type === 'white') {
          setNotification({
            message: 'Whiteout Active',
            type: 'active',
            visible: true
          });
        }
      }
    } catch (error) {
      console.error("Error checking overlay state:", error);
    }
    
    return () => {
      clearOverlayToastHide();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Watch for auto-advance state changes from control panel
  useEffect(() => {
    const checkAutoAdvance = () => {
      const saved = localStorage.getItem('deckpack-auto-advance');
      if (saved) {
        try {
          setAutoAdvance(JSON.parse(saved));
        } catch (e) {
          console.error('Error parsing auto-advance state:', e);
        }
      }
    };

    // Check immediately
    checkAutoAdvance();

    // Listen for changes
    const handleStorageChange = (e) => {
      if (e.key === 'deckpack-auto-advance') {
        checkAutoAdvance();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also poll as fallback (in case storage event doesn't fire for same-window updates)
    const interval = setInterval(checkAutoAdvance, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const commitSlideIndex = useCallback(
    (idx) => {
      if (!activePresentationId) return;
      const total = slideContentRef.current?.slides?.length ?? 0;
      const clamped = total > 0 ? Math.max(0, Math.min(idx, total - 1)) : Math.max(0, idx);
      const payload = {
        presentationId: activePresentationId,
        index: clamped,
        slideCount: total,
        timestamp: Date.now(),
      };
      try {
        localStorage.setItem('current-slide-state', JSON.stringify(payload));
        broadcastCurrentSlideState(payload);
      } catch {
        /* ignore */
      }
      setCurrentSlideIndex(clamped);
      setChromeSlideIndex(clamped);
      queueMicrotask(() => {
        try {
          presenterSlidesEmbedRef.current?.blurEmbeds?.();
        } catch {
          /* ignore */
        }
      });
    },
    [activePresentationId]
  );

  const bumpInSlideRevealAllPresenterWindows = useCallback(() => {
    try {
      presenterSlidesEmbedRef.current?.nudgeInSlideAdvance?.();
    } catch {
      /* ignore */
    }
    try {
      localStorage.setItem('deckpack-embed-reveal', String(Date.now()));
    } catch {
      /* ignore */
    }
  }, []);

  const presenterForwardBumpThenNext = useCallback(() => {
    bumpInSlideRevealAllPresenterWindows();
    const cur = currentSlideIndexRef.current;
    const total = slideContentRef.current?.slides?.length ?? 0;
    if (autoAdvanceRef.current && total > 0 && cur >= total - 1) {
      const next = advancePlaylistToNextDeckIfAtLastSlide({
        currentPresentationId: activePresentationId,
        currentSlideIndex: cur,
        deckSlideCount: total,
        autoAdvance: true,
      });
      if (next?.id) {
        setActivePresentationId(next.id);
        setCurrentSlideIndex(0);
        setChromeSlideIndex(0);
        return;
      }
    }
    commitSlideIndex(cur + 1);
  }, [activePresentationId, bumpInSlideRevealAllPresenterWindows, commitSlideIndex]);

  // Forward: Space / PageDown / → / ↓ (code + key) + period. Back: ← / ↑ / PageUp.
  useEffect(() => {
    const shouldDeferDeckKeys = (el) =>
      el &&
      typeof el.closest === 'function' &&
      el.closest('input, textarea, select, [contenteditable="true"]');

    const onKeyDown = (e) => {
      if (!activePresentationId) return;
      if (shouldDeferDeckKeys(e.target)) return;

      if (e.code === 'Period' || (e.key === '.' && !e.ctrlKey && !e.metaKey && !e.altKey)) {
        e.preventDefault();
        presenterForwardBumpThenNext();
        return;
      }

      const deckForward =
        e.code === 'Space' ||
        e.key === ' ' ||
        e.code === 'PageDown' ||
        e.code === 'ArrowRight' ||
        e.code === 'ArrowDown' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowDown';

      const deckBack =
        e.code === 'PageUp' ||
        e.code === 'ArrowLeft' ||
        e.code === 'ArrowUp' ||
        e.key === 'PageUp' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowUp';

      if (deckForward) {
        e.preventDefault();
        presenterForwardBumpThenNext();
        return;
      }

      if (deckBack) {
        e.preventDefault();
        commitSlideIndex(currentSlideIndexRef.current - 1);
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activePresentationId, commitSlideIndex, presenterForwardBumpThenNext]);

  return (
    <div className="presenter-view">
      <div className="presenter-main">
        <div className="current-slide">
          <div className="preview-overlay"></div>
          <div className="preview-container presenter-slide-preview-container">
            {/* Stack above iframe; .click-blocker blocks pointer access to Google embed (App.css). */}
            <div className="click-blocker" aria-hidden="true" />
            {activePresentationId ? (
              <DualGoogleSlidesEmbed
                ref={presenterSlidesEmbedRef}
                key={activePresentationId}
                src={presenterSlidesSrc}
                title="Google Slides presenter slide"
                onVisibleSlideReady={onPresenterEmbedVisibleReady}
                embedMuted
              />
            ) : null}
          </div>
        </div>
        <div ref={presenterNotesRef} className="presenter-notes">
          <div
            ref={notesContentRef}
            className="notes-content"
            dangerouslySetInnerHTML={{
              __html: slideContent?.slides[chromeSlideIndex]?.notes || " "
            }}
          />
        </div>
      </div>
      <div className="presenter-sidebar">
        <div className="presenter-sidebar-top">
          <div className="stopwatch-section">
            <h4>Presentation Time</h4>
            <div className="stopwatch">
              <div className="time-display">
                {new Date(time * 1000).toISOString().substr(11, 8)}
              </div>
            </div>
            <div className="system-time">
              {systemTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
            </div>
          </div>
        </div>
        <div className="slide-preview-section">
          <h4>Previous Slide</h4>
          <div className="slide-preview prev-slide">
            {chromeSlideIndex === 0 ? (
              <div className="slide-preview-boundary">
                <span className="deck-boundary-pill">Beginning</span>
              </div>
            ) : !slideContent?.slides ? (
              <div className="boundary-message">Loading slides…</div>
            ) : slideContent.slides[chromeSlideIndex - 1]?.thumbnail ? (
              <img 
                src={slideContent.slides[chromeSlideIndex - 1].thumbnail}
                alt="Previous slide"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="slide-thumbnail-placeholder">
                <span className="slide-placeholder-label">
                  Slide {slideContent.slides[chromeSlideIndex - 1]?.number ?? chromeSlideIndex}
                </span>
              </div>
            )}
            <button
              type="button"
              className="slide-preview-hit"
              disabled={!slideContent?.slides?.length || currentSlideIndex <= 0}
              aria-label="Go to previous slide"
              title="Previous slide"
              onClick={() => commitSlideIndex(currentSlideIndexRef.current - 1)}
            />
          </div>
        </div>

        <div className="slide-preview-section">
          <h4>Next Slide</h4>
          <div className="slide-preview next-slide">
            {!slideContent?.slides ? (
              <div className="boundary-message">Loading slides…</div>
            ) : chromeSlideIndex >= slideContent.slides.length - 1 ? (
              <div className="slide-preview-boundary slide-preview-boundary--stack">
                <span className="deck-boundary-pill">End</span>
                {autoAdvance ? (
                  <span className="deck-boundary-hint">Auto-advance on</span>
                ) : null}
              </div>
            ) : slideContent.slides[chromeSlideIndex + 1]?.thumbnail ? (
              <img 
                src={slideContent.slides[chromeSlideIndex + 1].thumbnail}
                alt="Next slide"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="slide-thumbnail-placeholder">
                <span className="slide-placeholder-label">
                  Slide {slideContent.slides[chromeSlideIndex + 1]?.number ?? chromeSlideIndex + 2}
                </span>
              </div>
            )}
            <button
              type="button"
              className="slide-preview-hit"
              disabled={
                !slideContent?.slides?.length ||
                currentSlideIndex >= slideContent.slides.length - 1
              }
              aria-label="Go to next slide"
              title="Next slide"
              onClick={() => presenterForwardBumpThenNext()}
            />
          </div>
        </div>
        
        {/* Add notification at the bottom of sidebar */}
        {notification.visible && (
          <div className={`control-notification ${notification.type}`}>
            {notification.message}
          </div>
        )}
      </div>
    </div>
  );
}
export default PresenterView;
